import {
  Box,
  CircularProgress,
  FormControlLabel,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
} from '@material-ui/core'
import { ButtonDialog } from 'components/controls/dialogs'
import { Button } from 'components/facets'
import { TablePaginationControls } from 'components/facets/TablePaginationControls'
import {
  backOfficeCreateFromLastOrderTransaction,
  backOfficeCreateNewRandomOrderTransaction,
  getFullOrderTransactionsData,
  getOrderTransactionPayload,
} from 'lib/api'
import { useConfirm } from 'lib/hooks'
import { useTransactionsSpinnerStore } from 'lib/hooks/useTransactionsSpinnerStore'
import { useSnackbar } from 'notistack'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AutoSizer, Column, Table } from 'react-virtualized'
import { ChannelFilterOptions, sleep } from 'shared'
import { Filter } from '../stockPricing/FilterDock/Filter'
import {
  getFormattedDate,
  listRowsPerPage,
  SortDirection,
  sortDirectionOptions,
} from './BackofficePresenter'

const isNonProductionEnv = () => {
  return process.env.NODE_ENV !== 'production' || window.location.hostname.includes('staging')
}

export const OrderTransactionsSearch = ({
  checkedUserId,
}: {
  checkedUserId?: string
}): JSX.Element => {
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const { setShowSpinnerLoading } = useTransactionsSpinnerStore()

  const [loading, setLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState<number>(0)
  const [rowsPerPage, setRowsPerPage] = useState(listRowsPerPage[0])
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')
  const [selectedChannel, setSelectedChannel] = useState(ChannelFilterOptions.cardmarket)
  const [searchedSourceOrderId, setSearchedSourceOrderId] = useState('')
  const [selectedNewOrderTransactionState, setSelectedNewOrderTransactionState] =
    useState<string>('')
  const [totalPages, setTotalPages] = useState<number>()
  const [transactions, setTransactions] = useState<any[]>([])
  const [hasSearched, setHasSearched] = useState(false)

  const [askCreateFakeOrder, CreateFakeOrderDialog] = useConfirm(
    t('common.confirm'),
    t('backOffice.orderTransactions.fakeOrderCreationConfirmMessage'),
    () => {
      handleCreateFakeOrder()
    }
  )

  const channelOptions = Object.values(ChannelFilterOptions)
    .slice(1)
    .map((channel) => (
      <MenuItem key={channel} value={channel}>
        {t(`common.channels.${channel}`)}
      </MenuItem>
    ))

  const handleSearchTransactions = async (page = currentPage) => {
    if (!checkedUserId) {
      enqueueSnackbar('select user search transactions', { variant: 'error' })
      return
    }

    setLoading(true)

    const params = {
      impersonatingUserId: checkedUserId,
      sourceOrderId: searchedSourceOrderId,
      channel: selectedChannel,
      sortDirection,
      size: rowsPerPage,
      page,
    }

    const response = await getFullOrderTransactionsData(params)
    setTransactions(response.data)
    setTotalPages(Math.ceil(response.totalCount / rowsPerPage))
    setHasSearched(true)
    setLoading(false)
  }

  const handleMoveToNewState = async (rowData: any) => {
    await backOfficeCreateFromLastOrderTransaction(
      selectedChannel,
      rowData.orderId,
      selectedNewOrderTransactionState
    )
    await sleep(1)
    await handleSearchTransactions()
    setShowSpinnerLoading(true)
  }

  const handleCreateFakeOrder = async () => {
    try {
      await backOfficeCreateNewRandomOrderTransaction(selectedChannel)
      await sleep(1)
      await handleSearchTransactions()
      setShowSpinnerLoading(true)
    } catch (err: any) {
      enqueueSnackbar(
        err.response?.data?.error ?? t('backOffice.orderTransactions.fakeOrderCreationError'),
        { variant: 'error' }
      )
    }
  }

  const downloadJsonFile = (data: any, filename = 'data.json') => {
    const jsonString = JSON.stringify(data, null, 2)
    const blob = new Blob([jsonString], { type: 'application/json' })
    const url = URL.createObjectURL(blob)

    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  const handleDownloadTransactionPayload = async (transaction: any) => {
    const params = {
      channel: selectedChannel,
      orderTransactionId: transaction.id,
    }

    const response = await getOrderTransactionPayload(params)

    downloadJsonFile(response)
  }

  return (
    <>
      <CreateFakeOrderDialog />

      <h4 className="my-4">OrderTransactions</h4>
      <Grid container spacing={3}>
        <Grid item xs={6} sm={2} md={2} lg={1} xl={1}>
          <Filter
            type="select"
            menuitems={channelOptions}
            setFilter={(_name, newValue) => setSelectedChannel(newValue)}
            defaultValue={selectedChannel}
            label={t(`common.channel`)}
            name={'channel'}
            activeFilter={false}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4} lg={2} xl={2}>
          <TextField
            type="text"
            label="Source order id"
            fullWidth
            value={searchedSourceOrderId}
            onChange={(e) => setSearchedSourceOrderId(e.target.value)}
          />
        </Grid>

        <Grid item xs={6} sm={2} md={2} lg={1} xl={1}>
          <Filter
            type="select"
            menuitems={sortDirectionOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
            setFilter={(_name, newValue) => setSortDirection(newValue)}
            defaultValue={sortDirection}
            label="Sort"
            name={'sort'}
            activeFilter={false}
          />
        </Grid>

        <Grid item xs={6} sm={2} md={2} lg={1} xl={1}>
          <Filter
            type="select"
            menuitems={listRowsPerPage.map((row) => (
              <MenuItem key={row} value={row}>
                {row}
              </MenuItem>
            ))}
            setFilter={(_name, newValue) => setRowsPerPage(newValue)}
            defaultValue={listRowsPerPage[0]}
            label="Rows"
            name={'rows'}
            activeFilter={false}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4} lg={2} xl={2}>
          <Button
            variant="contained"
            disabled={!checkedUserId}
            onClick={() => {
              setCurrentPage(0)
              handleSearchTransactions(0)
            }}
          >
            {t('backOffice.orderTransactions.searchOrderTransactions')}
          </Button>
        </Grid>
      </Grid>

      {isNonProductionEnv() && (
        <Grid container spacing={3}>
          <Grid item xs={6} sm={2} md={2} lg={1} xl={1}>
            <Button
              variant="contained"
              disabled={!checkedUserId || selectedChannel !== ChannelFilterOptions.cardmarket}
              onClick={askCreateFakeOrder}
            >
              {t('backOffice.orderTransactions.createFakeOrderForUser')}
            </Button>
          </Grid>
        </Grid>
      )}

      {loading ? (
        <div className="w-100 d-flex flex-column align-items-center ">
          <h6 className="pb-5">Loading</h6>
          <CircularProgress size={100} />
        </div>
      ) : transactions.length === 0 ? (
        hasSearched && (
          <span className="my-5 d-block text-center fw-bold" style={{ fontSize: '1.1rem' }}>
            No search results found
          </span>
        )
      ) : (
        <>
          <div
            className="transactions-table my-5"
            style={{ height: Math.min(transactions.length * 45 + 40 + 30, 500) }}
          >
            <span className="mb-1">{transactions.length} rows</span>
            <AutoSizer>
              {({ width }) => (
                <Table
                  width={Math.max(width, 1300)}
                  height={500}
                  headerHeight={40}
                  rowHeight={45}
                  rowCount={transactions.length}
                  rowGetter={({ index }) => transactions[index]}
                  className={`users-table`}
                >
                  <Column
                    width={width * 0.4}
                    label="Source Order Id"
                    dataKey="sourceOrderId"
                    cellRenderer={({ rowData: transaction }) => (
                      <Box width="100px" textAlign="center">
                        <span>{transaction.orderId}</span>
                      </Box>
                    )}
                  />
                  <Column
                    width={width * 0.4}
                    label={t('transaction.createdAt')}
                    dataKey="createdAt"
                    disableSort={true}
                    cellRenderer={({ rowData: transaction }) => (
                      <Box width="150px">{getFormattedDate(transaction.createdAt)}</Box>
                    )}
                  />

                  <Column
                    width={width * 0.4}
                    label={t('transaction.processedAt')}
                    dataKey="processedAt"
                    cellRenderer={({ rowData: transaction }) => (
                      <Box width="150px">
                        {' '}
                        {transaction.processedAt === null ? (
                          <>
                            <Tooltip title="Failed" placement="top">
                              <span className="processed-icon failed-icon">F</span>
                            </Tooltip>
                            {getFormattedDate(transaction.lastFailedAt)}
                          </>
                        ) : (
                          <>
                            <Tooltip title="Success" placement="top">
                              <span className="processed-icon success-icon">S</span>
                            </Tooltip>
                            {getFormattedDate(transaction.processedAt)}
                          </>
                        )}
                      </Box>
                    )}
                  />
                  <Column
                    width={width * 0.4}
                    label="Last error"
                    dataKey="last error"
                    cellRenderer={({ rowData: transaction }) => (
                      <Box width="100px" textAlign="center">
                        <span>{transaction.lastError}</span>
                      </Box>
                    )}
                  />
                  <Column
                    width={width * 0.4}
                    label={'From state - To state'}
                    dataKey="cause"
                    cellRenderer={({ rowData: transaction }) => (
                      <Box width="100px">
                        {transaction.fromState} &rArr; {transaction.toState}
                      </Box>
                    )}
                  />
                  <Column
                    width={width * 0.4}
                    label={'Retry count'}
                    dataKey="source"
                    cellRenderer={({ rowData: transaction }) => (
                      <Box width="100px">{transaction.retryCount}</Box>
                    )}
                  />

                  <Column
                    width={width * 0.4}
                    label={'Payload'}
                    dataKey="payload"
                    cellRenderer={({ rowData: transaction }) => (
                      <Box width="200px">
                        <Button
                          variant="outlined"
                          onClick={() => handleDownloadTransactionPayload(transaction)}
                        >
                          Download Payload
                        </Button>
                      </Box>
                    )}
                  />
                  {isNonProductionEnv() && (
                    <Column
                      width={width * 0.4}
                      label="Actions"
                      dataKey="actions"
                      cellRenderer={({ rowData }) => (
                        <ButtonDialog
                          label={t('backOffice.orderTransactions.moveToState')}
                          title={t('backOffice.orderTransactions.chooseNewState')}
                          content={() => (
                            <RadioGroup
                              value={selectedNewOrderTransactionState}
                              onChange={(e) => setSelectedNewOrderTransactionState(e.target.value)}
                            >
                              {[
                                'bought',
                                'paid',
                                'sent',
                                'received',
                                'evaluated',
                                'canceled',
                                'lost',
                              ].map((s) => (
                                <FormControlLabel
                                  key={s}
                                  value={s}
                                  control={<Radio />}
                                  label={t(`backOffice.orderTransactions.orderStates.${s}`)}
                                />
                              ))}
                            </RadioGroup>
                          )}
                          actions={(handleClose) => (
                            <>
                              <Button variant="outlined" onClick={handleClose}>
                                {t('common.close')}
                              </Button>
                              <Button
                                variant="contained"
                                color="primary"
                                disabled={!selectedNewOrderTransactionState}
                                onClick={() => {
                                  handleMoveToNewState(rowData)
                                  handleClose()
                                }}
                              >
                                {t('common.confirm')}
                              </Button>
                            </>
                          )}
                          renderComponent={(label, open) => (
                            <Button variant="outlined" color="primary" onClick={open}>
                              {label}
                            </Button>
                          )}
                        />
                      )}
                    />
                  )}
                </Table>
              )}
            </AutoSizer>
          </div>
          <TablePaginationControls
            currentPage={currentPage}
            setPage={(page) => {
              setCurrentPage(page)
              handleSearchTransactions(page)
            }}
            isLastPage={currentPage + 1 >= (totalPages ?? 0)}
            totalPages={totalPages}
            startFromZero={true}
          />
        </>
      )}
    </>
  )
}
